import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Mutation } from '@apollo/client/react/components';
import { Link } from 'react-router-dom';
import Helmet from 'helmet/helmet';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import ScrollToTop from 'lib/scroll/scroll-to-top';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import { Site } from 'four-nets/site';
import heart from 'four-nets/icons/svg/icon_heart.svg';
import edit from 'four-nets/icons/svg/icon_new-article.svg';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import Masonry, { MediaKey } from 'four-nets/photoblog/masonry/masonry';
import { MasonryFragment } from 'four-nets/photoblog/masonry/masonry.graphql';

import CollectionLikersDialog from '../collection-likers-dialog/collection-likers-dialog';
import likeMutation, {
    LikeCollectionMutation,
    LikeCollectionMutationVariables,
} from './mutations/like-collection.graphql';
import unlikeMutation, {
    UnlikeCollectionMutation,
    UnlikeCollectionMutationVariables,
} from './mutations/unlike-collection.graphql';
import CollectionInfo from './collection-info/collection-info';
import { CollectionFragment } from './collection.graphql';
import defaultStyles from './collection.scss';

const columnsSizes: Map<MediaKey, number> = new Map([
    ['sm', 2],
    ['md', 3],
    ['lg', 4],
]);

type ImageNodeEdges = NonNullable<MasonryFragment['edges']>;

export interface Props extends CollectionFragment {
    styles: typeof defaultStyles;
    fetchMore?: (all?: boolean) => Promise<any>;
    offset: number;
}

interface State {
    dialogOpen: boolean;
}

class Collection extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
        authCheck: PropTypes.func,
    };

    state = { dialogOpen: false };

    renderHelmet = (H: any) => {
        const { site } = this.context;
        const { title } = this.props;

        return (
            <FormattedMessage id='COLLECTIONS'>
                {message =>
                    title ? (
                        <H>
                            <title>
                                {title} - {message} | {site.name}
                            </title>
                        </H>
                    ) : (
                        <H>
                            <title>
                                {message} | {site.name}
                            </title>
                        </H>
                    )
                }
            </FormattedMessage>
        );
    };

    splitIntoCollumns = (array: any[] | null, columnCount: number = 4) => {
        if (!array) {
            return null;
        }

        const result: any[][] = new Array(columnCount);
        for (let i: number = 0; i < columnCount; i++) {
            result[i] = [];
        }
        let current = 0;

        array.forEach((element, index) => {
            result[current].push({ edge: element, index });
            current = (current + 1) % columnCount;
        });
        return result;
    };

    render() {
        const { styles, fetchMore, offset, ...collection } = this.props;

        const likeButton = (
            <Mutation<LikeCollectionMutation, LikeCollectionMutationVariables> mutation={likeMutation}>
                {like => (
                    <Mutation<UnlikeCollectionMutation, UnlikeCollectionMutationVariables> mutation={unlikeMutation}>
                        {unlike => (
                            <button
                                className={styles.likeButton}
                                onClick={() =>
                                    this.context.authCheck(() => {
                                        if (collection.isLikedByViewer) {
                                            unlike({ variables: { input: { id: collection.id } } });
                                        } else {
                                            like({ variables: { input: { id: collection.id } } });
                                        }
                                        return Promise.resolve();
                                    }, AuthOrigin.LikeImage)
                                }
                            >
                                <SVGIcon style={{ image: styles.likeIcon }} src={heart} />
                                {collection.isLikedByViewer ? (
                                    <FormattedMessage id='REMOVE_STAR' values={{ gender: 'v' }} />
                                ) : (
                                    <FormattedMessage id='ADD_STAR' values={{ gender: 'v' }} />
                                )}
                            </button>
                        )}
                    </Mutation>
                )}
            </Mutation>
        );
        const username = collection.photoblog.user && collection.photoblog.user.username;
        const editButton = (
            <Link to={`/blog/${username}/collection/${collection.slug}/edit/`} className={styles.editButton}>
                <SVGIcon style={{ image: styles.likeIcon }} src={edit} />
                <FormattedMessage id='EDIT_COLLECTION' />
            </Link>
        );

        return (
            <React.Fragment>
                <ScrollToTop offset={offset} />
                <CollectionLikersDialog
                    id={collection.id}
                    open={this.state.dialogOpen}
                    onClose={() => this.setState({ dialogOpen: false })}
                />
                <Helmet render={this.renderHelmet} />
                <div className={styles.titleRow}>
                    <div>
                        <h1 className={styles.title}>{collection.title}</h1>
                        <CollectionInfo {...collection} />
                    </div>
                    <ViewerConsumer>
                        {({ viewer }) => (
                            <div className={styles.buttons}>
                                {!viewer || (viewer && viewer.username !== username) ? likeButton : null}
                                {viewer && (viewer.username === username || viewer.isSuperstaff) ? editButton : null}
                            </div>
                        )}
                    </ViewerConsumer>
                </div>
                <Masonry
                    edges={collection.rawImages!.edges as ImageNodeEdges}
                    fetchMoreFn={fetchMore}
                    columnsSizes={columnsSizes}
                    disableLazyLoad={true}
                />
            </React.Fragment>
        );
    }
}

export default Styled(defaultStyles)(Collection);
